import React, { useState } from 'react';
import Textarea from 'react-textarea-autosize';

import { useMutation } from '@tanstack/react-query';
import pluralize from 'pluralize';

import {
  APIcreateComment,
  APIdeleteComment,
  APIupdateComment,
} from '../../api/Comment';

import MemoryCommentsList from './CommentsList';

import { isUserLoggedIn } from '../../helpers/auth';

import type { Comment } from '../../types/comment';

interface MemoryCommentsProps {
  memoryId: string;
  commentsAllowed: boolean;
  comments: Comment[];
  commentsCount: number;
  readOnly?: boolean;
}

const MemoryComments: React.FC<MemoryCommentsProps> = ({
  memoryId,
  commentsAllowed,
  comments: initialComments,
  commentsCount,
  readOnly = false,
}) => {
  const [comments, setComments] = useState<Comment[]>(initialComments);
  const [content, setContent] = useState('');

  if (!commentsAllowed) {
    return null;
  }

  const insertReplyByParentId = (commentsArray: Comment[], parentId: number, newComment: Comment): boolean => {
    for (const comment of commentsArray) {
      if (comment.id === parentId) {
        if (!comment.replies) {
          comment.replies = [];
        }
        comment.replies.unshift(newComment);
        return true;
      }

      if (comment.replies && comment.replies.length > 0) {
        const isInserted = insertReplyByParentId(comment.replies, parentId, newComment);
        if (isInserted) return true;
      }
    }
    return false;
  };

  const updateCommentById = (commentsArray: Comment[], targetId: number, newFields: Partial<Comment>): Comment | null => {
    for (const comment of commentsArray) {
      if (comment.id === targetId) {
        Object.assign(comment, newFields);
        return comment;
      }

      if (comment.replies && comment.replies.length > 0) {
        const updated: Comment | null = updateCommentById(comment.replies, targetId, newFields);
        if (updated) return updated;
      }
    }
    return null;
  };

  const removeCommentById = (commentsArray: Comment[], targetId: number): boolean => {
    for (let i = 0; i < commentsArray.length; i++) {
      if (commentsArray[i].id === targetId) {
        commentsArray.splice(i, 1);
        return true;
      }

      if (commentsArray[i].replies && commentsArray[i].replies.length > 0) {
        const isDeleted = removeCommentById(commentsArray[i].replies, targetId);
        if (isDeleted) return true;
      }
    }
    return false;
  };

  const { mutate: postComment, isPending: isPosting } = useMutation({
    mutationFn: async ({ content, parent_comment_id }: { content: string; parent_comment_id?: number }): Promise<{ response: Comment; parent_comment_id: number | null }> => {
      const response = await APIcreateComment(memoryId, { content, parent_comment_id });
      return { response: response.data, parent_comment_id: parent_comment_id || null };
    },
    onError: (error) => {
      console.error(error);
    },
    onSuccess: ({ response, parent_comment_id }: { response: Comment; parent_comment_id: number | null }) => {
      setComments((prevComments) => {
        setContent('');
        if (!parent_comment_id) {
          return [{ ...response, updated: true }, ...prevComments];
        }
        insertReplyByParentId(prevComments, parent_comment_id, { ...response, updated: true });
        return [...prevComments];
      });
    },
  });

  const { mutate: updateComment, isPending: isUpdating } = useMutation({
    mutationFn: async ({ commentId, content }: { commentId: number; content: string }): Promise<Comment> => {
      const response = await APIupdateComment(commentId, { content });
      return response.data;
    },
    onError: (error) => {
      console.error(error);
    },
    onSuccess: (response: Comment) => {
      setComments((prevComments) => {
        const newComments = [...prevComments];
        updateCommentById(newComments, response.id, { ...response, updated: true });
        return newComments;
      });
    },
  });

  const { mutate: deleteComment } = useMutation({
    mutationFn: async (commentId: number): Promise<{ commentId: number }> => {
      setComments((prevComments) => {
        const newComments = [...prevComments];
        updateCommentById(newComments, commentId, { updated: true });
        return newComments;
      });
      setTimeout(() => {
        setComments((prevComments) => {
          const newComments = [...prevComments];
          removeCommentById(newComments, commentId);
          return newComments;
        });
      }, 500);
      await APIdeleteComment(commentId);
      return { commentId };
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return (
    <>
      <hr />
      <div className="memory-comments">
        <div className="memory-comments-header wa-cluster wa-gap-xs">
          <i className="fa-regular fa-comment" />
          {commentsCount === 0 ? 'No comments yet' : `${commentsCount} ${pluralize('comment', commentsCount)}`}
        </div>
        {!readOnly && isUserLoggedIn() && (
          <div className="memory-comment-form">
            <Textarea
              id="new-comment"
              name="new-comment"
              onChange={(e) => setContent(e.target.value)}
              minRows={1}
              disabled={isPosting}
              value={content}
              placeholder="Say something about this memory..."
            />
            <wa-button onClick={() => postComment({ content })} size="s" disabled={isPosting || content.trim() === ''}>
              {isPosting ? 'Posting...' : 'Post'}
            </wa-button>
          </div>
        )}
        <MemoryCommentsList
          comments={comments}
          postComment={postComment}
          updateComment={updateComment}
          deleteComment={deleteComment}
          isPosting={isPosting}
          isUpdating={isUpdating}
          readOnly={readOnly}
        />
      </div>
    </>
  );
}

export default MemoryComments;
